import React, { useEffect, useMemo } from 'react';
import { ObservabilityErrorBoundary } from '@leagueplatform/observability';
import { subscribeToMessage } from '@leagueplatform/socket-as-fetch';
import { getScopedLegacyCssClassNames } from '@leagueplatform/web-common';
import { styled } from '@leagueplatform/genesis-core';
import { NodeResolver } from './components/node-resolver.component';
import { MasonryEngineConfigContext } from './masonry-engine-config-context';
import type {
  AnyMasonryEngineNodeAction,
  MasonryEngineNode,
} from './types/masonry-engine-node.types';
import { getMasonryEngineObservabilityContext } from './utils/masonry-engine-observability-content';
import { logError } from './utils/log';

import type { MasonryEngineDriver } from './types/masonry-engine-driver.types';
import { createInstanceResources } from './create-instance-resources';
import { type InstanceResources } from './types/masonry-engine-config.types';
import { HIDDEN_SUBTREE_CLASS_NAME } from './constants/constants';

const MasonryEngineContainer = styled('div', {
  [`.${HIDDEN_SUBTREE_CLASS_NAME}`]: {
    display: 'none',
  },
});

type MasonryServerActionWSMessage = {
  invoker: MasonryEngineNode['id'];
  action: AnyMasonryEngineNodeAction;
};

type MasonryEngineProps = {
  driver: MasonryEngineDriver<any, any, any>;
  instanceResources?: InstanceResources;
} & (
  | {
      rootNode: MasonryEngineNode;
      children?: never;
    }
  | {
      rootNode?: never;
      children: React.ReactNode;
    }
);

/**
 * The entry point to rendering a MasonryEngine flow. takes a {@link MasonryEngineDriver `MasonryEngineDriver`} and communicates
 * with the driver to render the correct screen.
 */
export const MasonryEngine = (props: MasonryEngineProps) => {
  const { driver, instanceResources } = props;
  // eslint-disable-next-line react/destructuring-assignment
  const rootNode = 'rootNode' in props ? props.rootNode : undefined;
  // eslint-disable-next-line react/destructuring-assignment
  const children = 'children' in props ? props.children : undefined;

  const { stateControllerStore, messagingController } = useMemo(
    () => instanceResources || createInstanceResources(),
    [instanceResources],
  );

  useEffect(() => {
    const unsubscribe = subscribeToMessage<MasonryServerActionWSMessage>(
      'masonry_action',
      ({ invoker, action }) => {
        messagingController.sendMessage(invoker, {
          type: 'serverAction',
          payload: {
            action,
          },
        });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [messagingController]);

  const classNames = getScopedLegacyCssClassNames();

  const config = useMemo(
    () => ({
      ...driver,
      stateControllerStore,
      messagingController,
    }),
    [driver, messagingController, stateControllerStore],
  );

  return (
    <ObservabilityErrorBoundary
      errorContext={getMasonryEngineObservabilityContext({
        tags: {
          errorName: `MasonryEngine - Error from Masonry Engine`,
        },
      })}
      onError={(err: Error) => {
        logError(`Error from Masonry Engine: ${err.message}`);
      }}
    >
      <MasonryEngineConfigContext.Provider value={config}>
        <MasonryEngineContainer
          data-testid="css-reset-wrapper"
          className={classNames}
        >
          {rootNode ? (
            <NodeResolver key={rootNode.id} node={rootNode} />
          ) : (
            children
          )}
        </MasonryEngineContainer>
      </MasonryEngineConfigContext.Provider>
    </ObservabilityErrorBoundary>
  );
};
